import { memo, useMemo } from 'react';
import { Link } from 'wouter';
import { useTranslation } from 'react-i18next';
import { Sparkles, TrendingUp, ArrowRight } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useSubscription } from '@/contexts/subscription-context';
import { PLANS, type PlanName, type SubscriptionLimits } from '@/types/subscription';

type UsageResource = keyof SubscriptionLimits['usage'];

interface UpgradeNudgeProps {
  isOpen: boolean;
  onClose: () => void;
  feature: UsageResource;
}

const PLAN_ORDER: PlanName[] = ['free', 'basic', 'pro', 'enterprise'];

/**
 * Maps usage resource keys to their corresponding Plan.features keys.
 */
function getFeatureLimitForPlan(planName: PlanName, feature: UsageResource): number | 'unlimited' {
  const plan = PLANS.find((p) => p.name === planName);
  if (!plan) return 0;

  switch (feature) {
    case 'services':
      return plan.features.services;
    case 'providers':
      return plan.features.providers;
    case 'users':
      // Users limit aligns with providers in the plan config
      return plan.features.providers;
    case 'bookingsThisMonth':
      return plan.features.bookings;
    default:
      return 0;
  }
}

function formatLimit(value: number | 'unlimited', t: (key: string) => string): string {
  if (value === 'unlimited' || value === Infinity) {
    return t('upgradeNudge.unlimited');
  }
  return String(value);
}

export const UpgradeNudge = memo(({ isOpen, onClose, feature }: UpgradeNudgeProps) => {
  const { t } = useTranslation('subscription');
  const { limits } = useSubscription();

  const currentPlanCode = (limits?.currentPlan?.code ?? 'free') as PlanName;

  const nextPlan = useMemo(() => {
    const currentIndex = PLAN_ORDER.indexOf(currentPlanCode);
    if (currentIndex === -1 || currentIndex >= PLAN_ORDER.length - 1) {
      return null;
    }
    const nextPlanName = PLAN_ORDER[currentIndex + 1];
    return PLANS.find((p) => p.name === nextPlanName) ?? null;
  }, [currentPlanCode]);

  const currentPlan = useMemo(() => {
    return PLANS.find((p) => p.name === currentPlanCode) ?? null;
  }, [currentPlanCode]);

  const currentLimit = limits?.usage[feature];
  const nextPlanLimit = nextPlan ? getFeatureLimitForPlan(nextPlan.name, feature) : null;

  const featureDisplayName = t(`limits.${feature === 'bookingsThisMonth' ? 'monthlyBookings' : feature}`);
  const currentPlanDisplayName = limits?.currentPlan?.displayName ?? currentPlan?.displayName ?? 'Free';
  const nextPlanDisplayName = nextPlan?.displayName ?? 'Pro';

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className="flex items-center justify-center w-12 h-12 rounded-full bg-amber-100">
              <Sparkles className="w-6 h-6 text-amber-600" />
            </div>
            <div className="flex-1">
              <DialogTitle className="text-xl">
                {t('upgradeNudge.title')}
              </DialogTitle>
              <Badge
                variant="outline"
                className="mt-1 text-xs font-normal border-muted-foreground/20"
              >
                {t('upgradeDialog.current', { plan: currentPlanDisplayName })}
              </Badge>
            </div>
          </div>
          <DialogDescription className="text-base text-foreground/80 pt-2">
            {t('upgradeNudge.limitMessage', {
              feature: featureDisplayName.toLowerCase(),
              plan: currentPlanDisplayName,
            })}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Current usage bar */}
          {currentLimit && (
            <div className="p-4 bg-muted/50 rounded-lg border border-border">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-muted-foreground">
                  {t('upgradeDialog.usage', { limitName: featureDisplayName })}
                </span>
                <span className="text-sm font-semibold">
                  {currentLimit.current} / {currentLimit.limit}
                </span>
              </div>
              <div className="w-full bg-background rounded-full h-2 overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-amber-500 to-red-500 transition-all duration-300"
                  style={{ width: `${Math.min(currentLimit.percentage, 100)}%` }}
                />
              </div>
            </div>
          )}

          {/* Next plan offer */}
          {nextPlan && nextPlanLimit !== null && (
            <div className="p-4 bg-primary/5 rounded-lg border border-primary/20">
              <div className="flex items-center gap-2 mb-2">
                <TrendingUp className="w-4 h-4 text-primary" />
                <span className="text-sm font-semibold text-primary">
                  {t('upgradeNudge.nextPlanOffer', { plan: nextPlanDisplayName })}
                </span>
              </div>
              <p className="text-sm text-foreground">
                {t('upgradeNudge.nextPlanDescription', {
                  plan: nextPlanDisplayName,
                  limit: formatLimit(nextPlanLimit, t),
                  feature: featureDisplayName.toLowerCase(),
                })}
              </p>
              {nextPlan.price.monthly > 0 && (
                <p className="text-xs text-muted-foreground mt-2">
                  {t('upgradeNudge.startingAt', {
                    price: nextPlan.price.monthly,
                  })}
                </p>
              )}
            </div>
          )}

          {/* Enterprise fallback when already on highest non-enterprise plan */}
          {!nextPlan && (
            <div className="p-4 bg-primary/5 rounded-lg border border-primary/20">
              <div className="flex items-center gap-2 mb-2">
                <TrendingUp className="w-4 h-4 text-primary" />
                <span className="text-sm font-semibold text-primary">
                  {t('upgradeNudge.contactSales')}
                </span>
              </div>
              <p className="text-sm text-foreground">
                {t('upgradeNudge.contactSalesDescription')}
              </p>
            </div>
          )}
        </div>

        <DialogFooter className="flex flex-col sm:flex-row gap-2">
          <Button variant="outline" onClick={onClose} className="sm:flex-1">
            {t('upgradeDialog.actions.maybeLater')}
          </Button>
          <Link href="/admin/billing" onClick={onClose}>
            <Button className="w-full sm:flex-1 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white">
              {nextPlan
                ? t('upgradeDialog.actions.upgradeTo', { plan: nextPlanDisplayName })
                : t('upgradeDialog.actions.upgradeToPremium')
              }
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
});

UpgradeNudge.displayName = 'UpgradeNudge';
